import { useEffect, useRef } from 'react';
import {
  ensureSecondaryAuthSynced,
  getDbSupabase,
  isSupabaseConfigured,
  supabase,
  type DbPortalKind,
} from '../lib/supabase';

export type PostgresChangeListen = {
  schema: string;
  table: string;
  filter?: string;
  event?: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
};

export type UsePostgresChangesRealtimeOptions = {
  enabled?: boolean;
  /** Stable channel name (avoid Date.now unless you need unique per mount). */
  channelName: string;
  /** Which DB client; ignored when usePrimaryClient is true. */
  portalKind?: DbPortalKind;
  /** Use the primary `supabase` client (e.g. access_management). */
  usePrimaryClient?: boolean;
  /** DEM / Forms secondary clients need JWT before Realtime RLS applies. */
  syncSecondaryAuth?: boolean;
  listens: PostgresChangeListen[];
  debounceMs?: number;
  onChange?: () => void;
};

/**
 * Shared postgres_changes + debounce reload helper used by portal domain hooks.
 */
export function usePostgresChangesRealtime(options: UsePostgresChangesRealtimeOptions) {
  const {
    enabled = true,
    channelName,
    portalKind = 'crm',
    usePrimaryClient = false,
    syncSecondaryAuth = false,
    listens,
    debounceMs = 500,
    onChange,
  } = options;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const timerRef = useRef<number | null>(null);
  const listensKey = JSON.stringify(listens);

  useEffect(() => {
    if (!enabled || !isSupabaseConfigured) return undefined;
    if (listens.length === 0) return undefined;

    let cancelled = false;
    let channel: ReturnType<NonNullable<typeof supabase>['channel']> | null = null;
    let retryTimer: number | null = null;
    let attempt = 0;

    const schedule = () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        onChangeRef.current?.();
      }, debounceMs);
    };

    const getDb = () =>
      usePrimaryClient ? supabase : getDbSupabase(portalKind);

    const teardownChannel = () => {
      if (!channel) return;
      const db = getDb();
      if (db) void db.removeChannel(channel);
      channel = null;
    };

    const connect = async () => {
      if (cancelled) return;

      if (syncSecondaryAuth) {
        await ensureSecondaryAuthSynced(4_000);
      }
      if (cancelled) return;

      const db = getDb();
      if (!db) return;

      // Ensure Realtime websocket carries the same JWT RLS uses for postgres_changes.
      try {
        const { data } = await db.auth.getSession();
        const token = data.session?.access_token;
        if (token) {
          await db.realtime.setAuth(token);
        }
      } catch {
        // Auth may still be warming; subscribe anyway and retry on failure.
      }
      if (cancelled) return;

      teardownChannel();

      let ch = db.channel(`${channelName}:${attempt}`);
      for (const listen of listens) {
        ch = ch.on(
          'postgres_changes',
          {
            event: listen.event ?? '*',
            schema: listen.schema,
            table: listen.table,
            ...(listen.filter ? { filter: listen.filter } : {}),
          },
          schedule,
        );
      }

      channel = ch.subscribe((status) => {
        if (cancelled) return;
        if (status === 'SUBSCRIBED') {
          attempt = 0;
          return;
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          if (attempt >= 5) return;
          const delay = Math.min(30_000, 1_000 * 2 ** attempt);
          attempt += 1;
          if (retryTimer != null) window.clearTimeout(retryTimer);
          retryTimer = window.setTimeout(() => {
            void connect();
          }, delay);
        }
      });
    };

    void connect();

    return () => {
      cancelled = true;
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      if (retryTimer != null) window.clearTimeout(retryTimer);
      teardownChannel();
    };
    // listensKey captures listens contents; channelName/portalKind gate identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    channelName,
    portalKind,
    usePrimaryClient,
    syncSecondaryAuth,
    debounceMs,
    listensKey,
  ]);
}
